import classNames from 'classnames'
import React, { useEffect, useCallback } from 'react'

import { Icon } from '../icon'

import styles from './drawer.module.scss'
import CloseIcon from './icon/cross.inline.svg'

interface DrawerProps {
  placement?: 'top' | 'right' | 'bottom' | 'left'
  open?: boolean
  /** Callback when the drawer is closed */
  onClose?: () => void
  /** Whether to show the mask */
  mask?: boolean
  /** Whether to close the drawer when the mask is clicked */
  maskClosable?: boolean
  /** Whether to show the close button */
  closable?: boolean
  title?: React.ReactNode
  footer?: React.ReactNode
  children: React.ReactNode
  width?: number | string
  height?: number | string
  zIndex?: number
  /** Whether to close the drawer when ESC key is pressed */
  keyboard?: boolean
  /** Callback when the drawer open state changes */
  afterOpenChange?: (open: boolean) => void
  /** Whether to destroy the drawer content when closed */
  destroyOnClose?: boolean
  /** Additional class name for the drawer */
  className?: string
  headerClassName?: string
  bodyClassName?: string
  footerClassName?: string
  closeIconClassName?: string
}

export const Drawer: React.FC<DrawerProps> = ({
  placement = 'left',
  open = false,
  onClose,
  mask = true,
  maskClosable = true,
  closable = true,
  title,
  footer,
  children,
  width,
  height,
  zIndex = 1000,
  keyboard = true,
  afterOpenChange,
  destroyOnClose = false,
  className,
  headerClassName,
  bodyClassName,
  footerClassName,
  closeIconClassName,
}) => {
  // Define constants for magic strings
  const PLACEMENTS = ['top', 'right', 'bottom', 'left'] as const
  const ESCAPE_KEY = 'Escape'

  // Handle overlay click to close the drawer
  const handleMaskClick = () => {
    if (maskClosable && onClose) {
      onClose()
    }
  }

  // Handle ESC key press to close the drawer
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === ESCAPE_KEY && keyboard && onClose) {
        onClose()
      }
    },
    [keyboard, onClose],
  )

  // Manage keyboard events and body scroll
  useEffect(() => {
    if (open && keyboard) {
      window.addEventListener('keydown', handleKeyDown)
      // document.body.classList.add('forceNoScroll')
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      // document.body.classList.remove('forceNoScroll')
    }
  }, [open, keyboard, handleKeyDown])

  useEffect(() => {
    if (afterOpenChange) {
      afterOpenChange(open)
    }
  }, [open, afterOpenChange])

  // Styles for overlay and drawer
  const overlayStyle = { zIndex }
  const drawerStyle = {
    zIndex: zIndex + 1,
    ...(width && (PLACEMENTS.includes(placement) ? { width } : {})),
    ...(height && (PLACEMENTS.includes(placement) ? { height } : {})),
  }

  return (
    <>
      {mask && (
        <div
          className={classNames(styles.drawerOverlay, open ? styles.open : '')}
          style={overlayStyle}
          onClick={handleMaskClick}
        />
      )}
      <div
        className={classNames(styles.drawer, styles[placement], open ? styles.open : '', className)}
        style={drawerStyle}
      >
        {title && (
          <div className={classNames(styles.drawerHeader, headerClassName)}>
            {title}
            {closable && (
              <button className={styles.drawerClose} onClick={onClose}>
                <Icon iconSvg={CloseIcon} className={classNames(styles.drawerCloseIcon, closeIconClassName)} />
              </button>
            )}
            <hr />
          </div>
        )}
        <div className={classNames(styles.drawerBody, bodyClassName)}>{open || !destroyOnClose ? children : null}</div>
        {footer && <div className={classNames(styles.drawerFooter, footerClassName)}>{footer}</div>}
      </div>
    </>
  )
}
